import React, { useState } from 'react';
import { Grid, Typography, IconButton, Button, CircularProgress, TextField, Tooltip, InputAdornment } from '@material-ui/core';
import AppLoader from '../../../../Components/AppLoader';
import { makeStyles } from '@material-ui/core/styles';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import LicenceTabsPanel, { LicenceRow, AllocatedCell, RowDeleteButton } from '../LicenceTabsPanel';
import { useStyle as onboardingStyle } from '../..';
import { CustomerServices } from '../../../../Services/CustomerServices';
import { ProjectServices } from '../../../../Services/ProjectServices';
import { IsNumeric, notify } from 'utils';
import { useParams } from 'react-router';
import variables from '../../../../Sys/variable.scss';
import { Autocomplete } from '@material-ui/lab';

const customerServices = new CustomerServices();
const projectServices = new ProjectServices();

const useStyles = makeStyles((theme) => ({
    removeBtn: {
        background: '#E23A3A',
        color: '#fff',
        height: 32,
        width: 32,
        padding: 0,
        borderRadius: '50%',
        '&:hover': {
            background: '#c20b0b',
            color: '#fff'
        }
    },
    addBtn: {
        height: 32,
        width: 32,
        padding: 0,
        borderRadius: '50%'
    },
    noHeading: {
        color: variables.darkThemeText,
        fontWeight: 400,
        fontSize: '10px',
        lineHeight: '12px',
        marginTop: 15
    },
    noOfItemWrap: {
        display: "flex",
        width: "100%",
        marginTop: 20
    },
    inputStyle: {
        minWidth: 130,
        width: '100%',
        marginTop: 16,
        marginLeft: 0
    },
    sectionLoaderWrap: {
        minHeight: 280,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    licencePool: {
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        marginBottom: 16,
        padding: '14px 20px',
        borderRadius: 12,
        backgroundColor: '#F3F6FC',
    },
    licencePoolHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
    },
    licencePoolLabel: {
        fontSize: '0.95rem',
        fontWeight: 500,
        color: '#647184',
    },
    licencePoolSummary: {
        fontSize: '0.95rem',
        color: '#647184',
    },
    licencePoolCount: {
        fontWeight: 700,
        color: '#1E4BB8',
        marginRight: 6,
    },
    licencePoolServices: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
    },
    licencePoolService: {
        flex: '1 1 140px',
        minWidth: 120,
        padding: '10px 14px',
        borderRadius: 10,
        backgroundColor: '#FFFFFF',
    },
    licencePoolServiceLabel: {
        fontSize: '0.8rem',
        fontWeight: 500,
        color: '#647184',
    },
    licencePoolServiceCount: {
        marginTop: 2,
        fontSize: '1.05rem',
        fontWeight: 700,
        color: '#1E4BB8',
    },
}));

export const AddMoreButton = ({ handleAddNew, action, disabled = false }) => {
    const classes = useStyles();
    return (
        <IconButton onClick={(evt) => {
            handleAddNew(action);
        }}
            className={classes.addBtn}
            aria-label={`add to more ${action}`}
            disabled={disabled}>
            <AddCircleIcon style={{ color: disabled ? "#B0B0B0" : "#07AD17", fontSize: '2.2rem' }} />
        </IconButton>
    )
}

export const RemoveButton = ({ handleClick, action, index }) => {
    const classes = useStyles();
    return (
        <IconButton
            onClick={(evt) => handleClick(action, index)}
            className={classes.removeBtn}
            aria-label={`remove ${action}`}
        >
            <HighlightOffIcon style={{ fontSize: '2.2rem' }} />
        </IconButton>
    )
}

export const NumberInput = ({ handleChange, value, index, action, maxNum, name, disabled }) => {
    const classes = useStyles();
    let inpuProps = { disableUnderline: true };
    if (maxNum !== null) {
        inpuProps.max = maxNum;
    }
    return (
        <div className={classes.noOfItemWrap}>
            <TextField
                variant="outlined"
                size="small"
                fullWidth
                label="Number of licenses"
                value={value}
                type="text"
                disabled={disabled || false}
                inputProps={{ ...inpuProps }}
                className={classes.inputStyle}
                name={name || "total_count"}
                onChange={evt => {
                    if (evt.target.value && !IsNumeric(evt.target.value)) {
                        return null;
                    }
                    handleChange(evt, index, action);
                }}
            />
        </div>
    );
}

const LicenceCountInput = ({ value, index, action, disabled, handleChange, allocatedCount }) => (
    <TextField
        variant="outlined"
        size="small"
        fullWidth
        label="Licences"
        value={value}
        type="text"
        name="total_count"
        disabled={disabled || false}
        onChange={evt => {
            if (evt.target.value && !IsNumeric(evt.target.value)) {
                return null;
            }
            handleChange(evt, index, action);
        }}
        InputProps={allocatedCount == null ? undefined : {
            endAdornment: (
                <InputAdornment position="end">
                    <Tooltip title={`Enter a value greater than ${allocatedCount}.`}>
                        <InfoOutlinedIcon style={{ fontSize: 18, color: '#3D7DDD', cursor: 'help' }} />
                    </Tooltip>
                </InputAdornment>
            ),
        }}
    />
);

export const LABS_TAB = {
    key: 'labs',
    label: 'Labs',
    singular: 'lab',
    canAdd: false,
    hideUnselected: true,
    columns: [['Lab', 5], ['Linked course', 3], ['Licences', 2], ['Allocated', 2]],
    subtitle: 'Labs are added automatically from selected courses that include labs.',
    emptyMessage: 'No labs yet. Select a course that includes labs to add them.',
};

const LICENCE_TABS = [
    { key: 'assessments', label: 'Assessments', singular: 'assessment', canAdd: true, columns: [['Assessment', 7], ['Licences', 2], ['Allocated', 2], ['', 1]] },
    { key: 'courses', label: 'Courses', singular: 'course', canAdd: true, alignCourses: true, columns: [['Provider', 2], ['Category', 3], ['Course', 3], ['Licences', 2], ['Allocated', 1], ['', 1]] },
    { key: 'apprenticeships', label: 'Apprenticeships', singular: 'apprenticeship', canAdd: false, columns: [['Apprenticeship', 7], ['Licences', 2], ['Allocated', 2], ['', 1]] },
    LABS_TAB,
];

const getRowLabel = (type, item) => {
    switch (type) {
        case 'assessments':
            return item?.assessment_id?.name || '';
        case 'courses':
            return `${item?.course_id?.name || ''} ${item?.provider || ''} ${item?.category || ''}`;
        case 'apprenticeships':
            return item?.apprenticeship_id?.name || '';
        case 'labs':
            return item?.name || '';
        default:
            return '';
    }
};

export const isRowSelected = (type, item) => {
    const key = { assessments: 'assessment_id', courses: 'course_id', apprenticeships: 'apprenticeship_id', labs: 'lab_id' }[type];
    return Boolean(item?.[key]);
};

export const initAssignLicenseFields = {
    assessments: [{ assessment_id: '', count: 0, total_count: 0, user_count: 0, maxCount: 0 }],
    courses: [{ course_id: '', count: 0, total_count: 0, provider: '', category: '', user_count: 0, maxCount: 0 }],
    apprenticeships: [{ apprenticeship_id: '', count: 0, total_count: 0, user_count: 0, maxCount: 0 }],
    labs: [{ lab_id: '', count: 0, name: '', total_count: 0, course_id: '', user_count: 0 }]
};

const initUserFields = {
    status: 'Open',
    project_name: '',
    company_id: '',
    company_admin_id: '',
    project_admin: '',
    description: ''
};

const AssignLicence = ({
    activeStep,
    handleBack,
    handleNext,
    apprenticeshipList,
    coursesList,
    assessmentList
}) => {

    const classes = useStyles();
    const onBorningClasses = onboardingStyle();
    const { id } = useParams();

    const [assignLicenseForm, setAssignLicenseForm] = useState({ ...initAssignLicenseFields });
    const [submitLoading, setSubmitLoading] = useState(false);
    const [user, setUser] = useState({ ...initUserFields });
    const [removeCourses, setRemoveCourses] = useState([]);
    const [removeApprenticeships, setRemoveApprenticeships] = useState([]);
    const [removeAssessments, setRemoveAssessments] = useState([]);
    const [existingLicence, setExistingLicence] = useState({ courses: [], assessments: [], apprenticeships: [] });
    const [removeLabs, setRemoveLabs] = useState([]);
    const [providersList, setProvidersList] = useState([]);
    const [categoriesList, setCategoriesList] = useState([]);
    const [providerCourseOptions, setProviderCourseOptions] = useState({});
    const [coursesFromAssignLicense, setCoursesFromAssignLicense] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [providerLoading, setProviderLoading] = useState(false);
    const [licencePool, setLicencePool] = useState({
        licences: 0,
        byService: { assessments: 0, courses: 0, apprenticeships: 0, labs: 0 },
    });
    React.useEffect(() => {
        getProjectDetails().finally(() => {
            setInitialLoading(false);
        });
    // Project details are loaded once for the fixed onboarding route.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function getProjectDetails() {
        const projectDetails = await projectServices.getProjectDetails(id || localStorage.getItem('cstmsrSaveFlow'));
        const reponseData = projectDetails?.data || projectDetails;
        if (reponseData) {
            const byService = {
                courses: Number(reponseData.total_course) || 0,
                assessments: Number(reponseData.total_assesment ?? reponseData.total_assessment) || 0,
                apprenticeships: Number(reponseData.total_apprenticeship) || 0,
                labs: Number(reponseData.total_lab) || 0,
            };
            setLicencePool({
                licences: Object.values(byService).reduce((sum, value) => sum + value, 0),
                byService,
            });
            const otherData = projectDetails?.other_data || reponseData?.other_data || {};
            const providers = otherData.providers || [];
            const allCoursesForProviders = otherData.course || [];
            const courseCategoryById = {};

            setProvidersList(Array.isArray(providers) ? providers : []);
            if (Array.isArray(allCoursesForProviders)) {
                allCoursesForProviders.forEach(cs => {
                    if (cs?.uuid) courseCategoryById[cs.uuid] = (cs.category || '').trim();
                });
            }
            const rawCategories = Array.isArray(otherData.categories) && otherData.categories.length
                ? otherData.categories
                : Object.values(courseCategoryById);
            setCategoriesList([...new Set(rawCategories.map(category => (category || '').trim()).filter(Boolean))]);

            // build initial mapping of provider -> courses for existing cards
            if (Array.isArray(allCoursesForProviders) && allCoursesForProviders.length) {
                const initialProviderCourseMap = {};
                const dropdownCourseOptions = allCoursesForProviders.map(cs => ({
                    ...cs,
                    id: cs.uuid,
                    name: (cs.provider === 'Xpert Skills') ? `${cs.name} - ${cs.provider}` : cs.name,
                    provider: cs.provider,
                    category: (cs.category || '').trim(),
                }));
                setCoursesFromAssignLicense(dropdownCourseOptions);
                allCoursesForProviders.forEach(cs => {
                    const providerName = cs?.provider;
                    if (!providerName) return;
                    if (!initialProviderCourseMap[providerName]) {
                        initialProviderCourseMap[providerName] = [];
                    }
                    initialProviderCourseMap[providerName].push({
                        ...cs,
                        id: cs.uuid,
                        name: cs.name,
                        provider: cs.provider,
                        category: (cs.category || '').trim(),
                    });
                });
                setProviderCourseOptions(initialProviderCourseMap);
            }
            const { assessment, apprenticeship, course, lab } = reponseData;
            let courseIds = [];
            let assesmentIds = [];
            let apprenticeshipIds = [];
            const courseArray = (course && course.length > 0)
                ? course
                : assignLicenseForm.courses;
            const courseResult = [];
            courseArray.forEach(item => {
                courseResult.push({
                    ...item,
                    course_id: {
                        id: item.uuid,
                        name: (item.provider === 'Xpert Skills') ? `${item.name} - ${item.provider}` : item.name,
                        category: courseCategoryById[item.uuid] || item.category || '',
                    },
                    count: item.count ? parseInt(item.count) : 0,
                    provider: item.provider || '',
                    category: courseCategoryById[item.uuid] || item.category || '',
                });
                courseIds.push(item.uuid);
            });
            let assessments = (assessment && assessment?.length) ?
                assessment.map(itm => {
                    assesmentIds.push(itm.uuid);
                    return ({
                        assessment_id: itm,
                        count: parseInt(itm.count),
                        ...itm
                    });
                }) : assignLicenseForm.assessments;
            let apprenticeships = (apprenticeship && apprenticeship?.length) ?
                apprenticeship.map(itm => {
                    apprenticeshipIds.push(itm.uuid);
                    return ({
                        apprenticeship_id: itm,
                        count: parseInt(itm.count),
                        ...itm
                    });
                }) : assignLicenseForm.apprenticeships;
            const mappedLabs = await projectServices.getLabsByCourse({ course_ids: courseIds });
            let labs = (lab && lab?.length) ?
                lab.map(itm => {
                    const selectedLab = (mappedLabs?.data) && mappedLabs.data.find(lb => lb?.uuid === itm?.uuid);
                    return {
                        lab_id: itm,
                        course_id: selectedLab?.course_id || '',
                        count: parseInt(itm.count),
                        ...itm
                    }
                }) : assignLicenseForm.labs;
            setUser({
                project_name: reponseData.project_name,
                status: reponseData?.status || "Open",
                description: reponseData.description,
                company_id: reponseData.company_id,
                company_admin_id: reponseData.company_admin_id,
                project_admin: reponseData.project_admin
            });
            if (!lab.length) {
                if (mappedLabs?.data) {
                    const selectedLabs = (mappedLabs?.data.length) ? mappedLabs?.data.map(itm => {
                        let selectedCourse = courseResult.find(crs => crs?.course_id?.id === itm?.course_id);
                        return {
                            lab_id: { id: itm.uuid, name: itm.name },
                            name: itm.name,
                            count: selectedCourse?.count || 0,
                            course_id: itm?.course_id || '',
                            total_count: selectedCourse?.total_count || 0
                        };
                    }) : [...initAssignLicenseFields.labs];
                    labs = selectedLabs;
                }
            }
            setExistingLicence({ courses: courseIds, assessments: assesmentIds, apprenticeships: apprenticeshipIds });
            setAssignLicenseForm({ labs: labs, courses: courseResult, assessments: assessments, apprenticeships: apprenticeships });

        }
    }

    const loadCoursesForProvider = async (providerName) => {
        if (!providerName) {
            return [];
        }

        if (providerCourseOptions[providerName]) {
            return providerCourseOptions[providerName];
        }

        setProviderLoading(true);
        try {
            const projectDetails = await projectServices.getProjectDetails(id || localStorage.getItem('cstmsrSaveFlow'), providerName);
            const reponseData = projectDetails?.data || projectDetails;
            const allCourses = (projectDetails?.other_data?.course || reponseData?.other_data?.course) || [];

            const mappedCourses = allCourses
                .filter(cs => cs?.provider === providerName)
                .map(cs => ({
                    ...cs,
                    id: cs.uuid,
                    name: cs.name,
                    provider: cs.provider,
                    category: (cs.category || '').trim(),
                }));

            setProviderCourseOptions(prev => ({
                ...prev,
                [providerName]: mappedCourses
            }));

            return mappedCourses;
        } finally {
            setProviderLoading(false);
        }
    };

    const handleChangeInput = (e, idx, type) => {
        const { name, value } = e.target;
        let newVal = value;
        if (name === 'total_count') {
            newVal = value ? parseInt(value) : 0;
        }
        setAssignLicenseForm((prevState) => {
            let newState = { ...prevState };
            let prvData = [...newState[type]];
            prvData[idx] = { ...prvData[idx], [name]: newVal };
            newState[type] = prvData;
            return newState;
        });
    };

    const handleChangeAutoComplete = (value, idx, type, name) => {
        const newVal = value;
        setAssignLicenseForm((prevState) => {
            let newState = { ...prevState };
            let prvData = [...newState[type]];
            prvData[idx] = { ...prvData[idx], [name]: newVal };
            newState[type] = prvData;
            return newState;
        });
    };

    const handleRemoveAssignLicence = (type, idx) => {
        let checkExist = assignLicenseForm?.[type]?.[idx];
        let checkKey = (type === 'courses') ? 'course_id' : (type === 'apprenticeships') ? 'apprenticeship_id' : (type === 'assessments') ? 'assessment_id' : '';
        if (checkKey && checkExist[checkKey] && existingLicence[type].includes(checkExist[checkKey]?.id || checkExist[checkKey]?.uuid)) {
            if (type === 'courses') {
                setRemoveCourses([...removeCourses, checkExist?.course_id?.id]);
            } else if (type === 'apprenticeships') {
                setRemoveApprenticeships([...removeApprenticeships, checkExist?.apprenticeship_id?.uuid]);
            } else if (type === 'assessments') {
                setRemoveAssessments([...removeAssessments, checkExist?.assessment_id?.uuid]);
            }
        }
        if (type === 'courses') {
            autoSelectLabsByCourse(null, idx, 'remove');
        } else {
            setAssignLicenseForm((prevState) => {
                let newState = { ...prevState };
                let prvData = newState[type];
                prvData.splice(idx, 1);
                newState[type] = (prvData?.length) ? prvData : [...initAssignLicenseFields[type]];
                return newState;
            });
        }
    }

    const handleAddAssignLicence = (type) => {
        setAssignLicenseForm((prevState) => {
            let newState = { ...prevState };
            let prvData = [...newState[type]];
            prvData.push({ ...initAssignLicenseFields[type][0] });
            newState[type] = prvData;
            return newState;
        });
    }

    const autoSelectLabsByCourse = async (course, indx, action = 'add') => {
        let removeCourse = (course === null && assignLicenseForm.courses.length) ? { ...assignLicenseForm.courses[indx] } : null;
        const courseIds = (removeCourse && removeCourse?.course_id) ? [removeCourse?.course_id?.id] : (course?.id) ? [course?.id] : [];
        let selectedLabs = (assignLicenseForm.labs.length && assignLicenseForm.labs[0].lab_id) ? [...assignLicenseForm.labs] : [];
        let removeLabIds = [...removeLabs];
        // Labs come from the course-to-lab mapping for any provider; courses without labs return none.
        if (action === 'add' && courseIds.length) {
            const courseLabs = await projectServices.getLabsByCourse({ course_ids: courseIds });
            if (courseLabs?.data) {
                courseLabs.data.forEach(itm => {
                    if (selectedLabs.some(lab => (lab.lab_id?.id || lab.lab_id) === itm.uuid)) return;
                    selectedLabs.push({
                        lab_id: { id: itm.uuid, name: itm.name },
                        name: itm.name,
                        count: course?.count || 0,
                        total_count: course?.total_count || 0,
                        course_id: itm?.course_id || ''
                    });
                });
            }
        } else if (action !== 'add' && removeCourse && removeCourse.course_id) {
            const removeLabsIndex = [];
            assignLicenseForm.labs.forEach((lab, idx) => {
                if (lab.course_id === removeCourse.course_id?.id) {
                    removeLabsIndex.push(idx);
                    removeLabIds.push(lab?.uuid);
                }
            });
            if (removeLabsIndex.length) {
                const indexSet = new Set(removeLabsIndex);
                selectedLabs = selectedLabs.filter((value, i) => !indexSet.has(i));
                setRemoveLabs(removeLabIds);
            }
        }
        setAssignLicenseForm((prevState) => {
            let newState = { ...prevState };
            let prvData = [...newState.courses];
            if (action === 'add') {
                prvData[indx] = {
                    ...prvData[indx],
                    course_id: course,
                    // pre-populate provider and category from the selected course when available
                    provider: course?.provider || prvData[indx]?.provider || '',
                    category: course?.category || prvData[indx]?.category || ''
                };
            } else {
                prvData.splice(indx, 1);
            }
            newState.labs = (selectedLabs.length) ? [...selectedLabs] : [...initAssignLicenseFields.labs];
            newState.courses = (prvData.length) ? prvData : [...initAssignLicenseFields.courses];
            return newState;
        });
    }

    const renderAssessmentRow = (item, key) => (
        <LicenceRow>
            <Grid item xs={12} md={7}>
                <Autocomplete
                    id={`assessment-${key}`}
                    value={item.assessment_id || null}
                    name="assessment_id"
                    options={assessmentList}
                    getOptionLabel={option => option.name}
                    onChange={(event, newValue) => {
                        let check = assignLicenseForm.assessments.find(cr => (cr?.assessment_id?.uuid === newValue?.uuid && newValue?.uuid));
                        if (check) {
                            notify('error', `This assessment is already selected!`);
                            return null;
                        }
                        handleChangeAutoComplete(newValue, key, 'assessments', 'assessment_id');
                    }}
                    renderInput={(params) => (
                        <TextField {...params} variant="outlined" size="small" fullWidth label="Select Assessment" />
                    )}
                />
            </Grid>
            <Grid item xs={5} md={2}>
                <LicenceCountInput
                    value={item.total_count ? parseInt(item.total_count) : 0}
                    index={key}
                    action="assessments"
                    allocatedCount={parseInt(item.count) || 0}
                    handleChange={handleChangeInput}
                />
            </Grid>
            <Grid item xs={5} md={2}>
                <AllocatedCell count={item.count} total={item.total_count} />
            </Grid>
            <RowDeleteButton action="assessments" index={key} label="assessment" handleClick={handleRemoveAssignLicence} hidden={parseInt(item.count, 10) > 0} />
        </LicenceRow>
    );

    const providersForCategory = (categoryName) => {
        const category = (categoryName || '').trim();
        if (!category) return providersList;
        const names = [...new Set(
            (coursesFromAssignLicense || [])
                .filter(course => (course?.category || '').trim() === category)
                .map(course => course.provider)
                .filter(Boolean)
        )];
        const ordered = providersList.filter(provider => names.includes(provider));
        return ordered.length ? ordered : names.sort((a, b) => a.localeCompare(b));
    };

    const categoriesForRow = (item) => {
        const providerCourses = item.provider && providerCourseOptions[item.provider];
        if (providerCourses?.length) {
            const names = [...new Set(providerCourses.map(course => (course.category || '').trim()).filter(Boolean))];
            if (names.length) return names.sort((a, b) => a.localeCompare(b));
        }
        return categoriesList;
    };

    const courseOptionsForRow = (item) => {
        const selectedCategory = (item.category || '').trim();
        const providerCourses = item.provider && providerCourseOptions[item.provider];
        const catalog = coursesFromAssignLicense.length ? coursesFromAssignLicense : (coursesList || []);
        const base = providerCourses?.length ? providerCourses : catalog;
        if (!selectedCategory) return base;

        const matchesCategory = (course) => (course?.category || '').trim() === selectedCategory;
        const fromProvider = base.filter(matchesCategory);
        if (fromProvider.length || !providerCourses?.length) return fromProvider;

        return catalog.filter(course => matchesCategory(course) && (!item.provider || course.provider === item.provider));
    };

    const renderCourseRow = (item, key) => (
        <LicenceRow>
            <Grid item xs={12} md={2}>
                <Autocomplete
                    key={`provider-${key}-${item.category || ''}`}
                    id={`provider-${key}`}
                    value={item.provider || null}
                    name="provider"
                    options={item.category ? providersForCategory(item.category) : providersList}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option) || ''}
                    onChange={async (event, newValue) => {
                        const providerName = newValue || '';
                        setAssignLicenseForm(prevState => {
                            const newState = { ...prevState };
                            const courses = [...newState.courses];
                            const current = courses[key] || {};
                            const categoryStillApplies = !current.category
                                || !providerName
                                || providersForCategory(current.category).includes(providerName);
                            const courseProvider = current?.course_id?.provider || '';
                            const courseCategory = (current?.course_id?.category || '').trim();
                            const keepCourse = Boolean(current.course_id)
                                && (!providerName || courseProvider === providerName)
                                && (!current.category || courseCategory === current.category.trim());
                            courses[key] = {
                                ...current,
                                provider: providerName,
                                category: categoryStillApplies ? current.category : '',
                                course_id: keepCourse ? current.course_id : '',
                            };
                            newState.courses = courses;
                            return newState;
                        });
                        if (providerName) {
                            await loadCoursesForProvider(providerName);
                        }
                    }}
                    renderInput={(params) => (
                        <TextField {...params} variant="outlined" size="small" fullWidth label="Select Provider" />
                    )}
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <Autocomplete
                    id={`category-${key}`}
                    value={item.category || null}
                    name="category"
                    options={categoriesForRow(item)}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option) || ''}
                    onChange={(event, newValue) => {
                        const categoryName = (typeof newValue === 'string' ? newValue : newValue?.name || '').trim();
                        const matchingProviders = providersForCategory(categoryName);
                        setAssignLicenseForm(prevState => {
                            const newState = { ...prevState };
                            const courses = [...newState.courses];
                            const current = courses[key] || {};
                            const currentProvider = current.provider || '';
                            let nextProvider = currentProvider;
                            if (categoryName && matchingProviders.length === 1) {
                                nextProvider = matchingProviders[0];
                            } else if (categoryName && currentProvider && !matchingProviders.includes(currentProvider)) {
                                nextProvider = '';
                            }
                            const courseCategory = (current?.course_id?.category || '').trim();
                            const courseProvider = current?.course_id?.provider || '';
                            const keepCourse = courseCategory === categoryName
                                && (!nextProvider || courseProvider === nextProvider);
                            courses[key] = {
                                ...current,
                                category: categoryName,
                                provider: nextProvider,
                                course_id: keepCourse ? current.course_id : '',
                            };
                            newState.courses = courses;
                            return newState;
                        });
                        if (matchingProviders.length === 1) {
                            loadCoursesForProvider(matchingProviders[0]);
                        }
                    }}
                    renderInput={(params) => (
                        <TextField {...params} variant="outlined" size="small" fullWidth label="Select Category" />
                    )}
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <Autocomplete
                    key={`course-${key}-${item.provider || ''}-${item.category || ''}`}
                    id={`course-${key}`}
                    value={item.course_id || null}
                    name="course_id"
                    options={courseOptionsForRow(item)}
                    getOptionLabel={option => option?.name || ''}
                    getOptionSelected={(option, selected) => option?.id === selected?.id}
                    onChange={(event, newValue) => {
                        let check = assignLicenseForm.courses.find(cr => cr?.course_id?.id === newValue?.id && newValue?.id);
                        if (check) {
                            notify('error', `This course is already selected!`);
                            return null;
                        }
                        return autoSelectLabsByCourse(newValue, key, 'add');
                    }}
                    renderInput={(params) => (
                        <TextField {...params} variant="outlined" size="small" fullWidth label="Select Course" />
                    )}
                />
            </Grid>
            <Grid item xs={5} md={2}>
                <LicenceCountInput
                    value={item.total_count ? parseInt(item.total_count) : 0}
                    index={key}
                    action="courses"
                    allocatedCount={parseInt(item.count) || 0}
                    handleChange={handleChangeInput}
                />
            </Grid>
            <Grid item xs={5} md={1}>
                <AllocatedCell count={item.count} total={item.total_count} />
            </Grid>
            <RowDeleteButton action="courses" index={key} label="course" handleClick={handleRemoveAssignLicence} hidden={parseInt(item.count, 10) > 0} />
        </LicenceRow>
    );

    const renderApprenticeshipRow = (item, key) => (
        <LicenceRow>
            <Grid item xs={12} md={7}>
                <Autocomplete
                    id={`apprenticeship-${key}`}
                    value={item.apprenticeship_id || null}
                    name="apprenticeship_id"
                    options={apprenticeshipList}
                    getOptionLabel={option => option.name}
                    onChange={(event, newValue) => {
                        let check = assignLicenseForm.apprenticeships.find(cr => (cr?.apprenticeship_id?.uuid === newValue?.uuid && newValue?.uuid));
                        if (check) {
                            notify('error', `This apprenticeship is already selected!`);
                            return null;
                        }
                        return handleChangeAutoComplete(newValue, key, 'apprenticeships', 'apprenticeship_id');
                    }}
                    renderInput={(params) => (
                        <TextField {...params} variant="outlined" size="small" fullWidth label="Select Apprenticeship" />
                    )}
                />
            </Grid>
            <Grid item xs={5} md={2}>
                <LicenceCountInput
                    value={item.total_count ? parseInt(item.total_count) : 0}
                    index={key}
                    action="apprenticeships"
                    allocatedCount={parseInt(item.count) || 0}
                    handleChange={handleChangeInput}
                />
            </Grid>
            <Grid item xs={5} md={2}>
                <AllocatedCell count={item.count} total={item.total_count} />
            </Grid>
            <RowDeleteButton action="apprenticeships" index={key} label="apprenticeship" handleClick={handleRemoveAssignLicence} hidden={parseInt(item.count, 10) > 0} />
        </LicenceRow>
    );

    const renderLabRow = (item, key) => {
        const linkedCourse = assignLicenseForm.courses.find(crs => crs?.course_id?.id && crs.course_id.id === item.course_id);
        return (
            <LicenceRow>
                <Grid item xs={12} md={5}>
                    <TextField variant="outlined" size="small" fullWidth label="Lab" value={item.name || ''} disabled />
                </Grid>
                <Grid item xs={12} md={3}>
                    <Typography variant="body2">{linkedCourse?.course_id?.name || '—'}</Typography>
                </Grid>
                <Grid item xs={6} md={2}>
                    <LicenceCountInput
                        value={item.total_count ? parseInt(item.total_count) : 0}
                        index={key}
                        action="labs"
                        disabled
                        handleChange={handleChangeInput}
                    />
                </Grid>
                <Grid item xs={6} md={2}>
                    <AllocatedCell count={item.count} total={item.total_count} />
                </Grid>
            </LicenceRow>
        );
    };

    const rowRenderers = {
        assessments: renderAssessmentRow,
        courses: renderCourseRow,
        apprenticeships: renderApprenticeshipRow,
        labs: renderLabRow,
    };

    const formValidationCheck = async (evt) => {
        const {
            assessments,
            courses,
            apprenticeships,
            labs
        } = assignLicenseForm;
        let isValidCourseCount = [];
        let isValidAsseCount = [];
        let isValidApprCount = [];
        const payload = {
            remove_assesments: removeAssessments,
            remove_courses: removeCourses,
            remove_apprenticeships: removeApprenticeships,
            remove_labs: removeLabs,
            status: user.status,
            customer_id: id || localStorage.getItem('cstmsrSaveFlow'),
            course_customer: (courses.length && courses[0]?.course_id) ? courses.map(itm => {
                if (itm?.course_id?.id && !itm.total_count) {
                    isValidCourseCount.push(itm);
                }
                return { ...itm, count: itm.total_count, course_id: itm.course_id.id };
            }) : [],
            assessment_customer: (assessments.length && assessments[0]?.assessment_id) ? assessments.map(itm => {
                if (itm?.assessment_id?.uuid && !itm.total_count) {
                    isValidAsseCount.push(itm);
                }
                return ({ ...itm, count: itm.total_count, assessment_id: itm.assessment_id.uuid })
            }) : [],
            apprenticeship_customer: (apprenticeships.length && apprenticeships[0].apprenticeship_id) ? apprenticeships.map(itm => {
                if (!itm.total_count) {
                    isValidApprCount.push(itm);
                }
                return ({ ...itm, count: itm.total_count, apprenticeship_id: itm.apprenticeship_id.uuid });
            }) : [],
            lab_customer: (labs.length && labs[0]?.lab_id) ? labs.map(itm => {
                let course = courses.find(crs => crs?.course_id?.id === itm.course_id);
                return {
                    ...itm,
                    count: course?.total_count || 0,
                    total_count: course?.total_count || 0,
                    lab_id: itm?.uuid || itm?.lab_id?.id || ''
                };
            }) : []
        };
        if (isValidAsseCount.length) {
            let asses = isValidAsseCount.map(cs => cs?.assessment_id?.name || '');
            notify("error", `You can't Assign ${asses.join(", ")} Assessment with 0 Count!`);
            return null;
        }
        if (isValidCourseCount.length) {
            let courses = isValidCourseCount.map(cs => cs?.course_id?.name || '');
            notify("error", `You can't Assign ${courses.join(", ")} Course with 0 Count!`);
            return null;
        }
        if (isValidApprCount.length) {
            let appris = isValidApprCount.map(cs => cs?.apprenticeship_id?.name || '');
            notify("error", `You can't Assign ${appris.join(", ")} Apprenticeship with 0 Count!`);
            return null;
        }
        try {
            setSubmitLoading(true);
            const result = await customerServices.assignCustomerLicence(payload, id || localStorage.getItem('cstmsrSaveFlow'));
            if (result && result.status) {
                if (result && result.message.includes('associated with user')) {
                    setSubmitLoading(false);
                    setRemoveCourses([]);
                    setRemoveAssessments([]);
                    setRemoveApprenticeships([]);
                    notify("error", "Some Courses/Assessments/Apprenticeships are already assigned with projects/users, You can't remove it, Please Unallocated from there and try.");
                    return getProjectDetails();
                } else {
                    localStorage.setItem('assign_lecense', true);
                    localStorage.setItem('refreshlic', true);
                    localStorage.setItem('getAllCustomer', true);
                    handleNext(evt);
                    setSubmitLoading(false);
                    notify("success", result.message);
                    if (id) {
                        localStorage.setItem('editstep', 2);
                    }
                }
            } else {
                setSubmitLoading(false);
                notify("error", (result && result.message) ? result.message : 'Something Went Wrong');
            }
        }
        catch (err) {
            setSubmitLoading(false);
            console.log("error", err);
        }
    }

    return (
        <React.Fragment>
            {(initialLoading || providerLoading) ? (
                <AppLoader minHeight={280} />
            ) : null}
            {!(initialLoading || providerLoading) ? (
            <>
            <div className={classes.licencePool}>
                <div className={classes.licencePoolHeader}>
                    <Typography className={classes.licencePoolLabel}>Client licence pool</Typography>
                    <Typography className={classes.licencePoolSummary}>
                        <span className={classes.licencePoolCount}>
                            {licencePool.licences.toLocaleString()} {licencePool.licences === 1 ? 'licence' : 'licences'}
                        </span>
                        across 4 services
                    </Typography>
                </div>
                <div className={classes.licencePoolServices}>
                    {LICENCE_TABS.map((tab) => (
                        <div key={tab.key} className={classes.licencePoolService}>
                            <Typography className={classes.licencePoolServiceLabel}>{tab.label}</Typography>
                            <Typography className={classes.licencePoolServiceCount}>
                                {(licencePool.byService[tab.key] || 0).toLocaleString()}
                            </Typography>
                        </div>
                    ))}
                </div>
            </div>
            <LicenceTabsPanel
                tabs={LICENCE_TABS}
                items={assignLicenseForm}
                isRowSelected={isRowSelected}
                getRowLabel={getRowLabel}
                renderRow={(type, item, index) => rowRenderers[type](item, index)}
                onAdd={handleAddAssignLicence}
            />
            </>
            ) : null}
            <div className={onBorningClasses.buttons}>
                <div className={onBorningClasses.wrapper}>
                    {activeStep !== 0 && (
                        <Button
                            onClick={handleBack}
                                    variant="outlined"
                                    color="default"
                            className={onBorningClasses.cancelBtn}>
                            Back
                        </Button>
                    )}
                    <Button
                        type={"button"}
                        onClick={formValidationCheck}
                        variant="contained"
                        color="primary"
                        disabled={submitLoading}
                        className={onBorningClasses.button}
                    >
                        {submitLoading ? (
                            <CircularProgress
                                size={18}
                                className={classes.buttonProgress}
                            />
                        ) : 'Next'}
                    </Button>
                </div>
            </div>
        </React.Fragment>
    );
};

export default React.memo(AssignLicence);
